import React from 'react';
import {observer} from 'mobx-react';
import DateRangeSelector from './DateRangeSelector';
import FFDropdownList from './FFDropdownList';
import {currencySymbol, formatValue, numberFormat, percentFormat, ValueFormat} from '../utility/FormatValueUtils';
import {OverviewStatus, DiagramType} from '../stores/DashboardStore';
import {DateUtils} from '../utility/DateUtils';
import scrollbarSize from 'dom-helpers/util/scrollbarSize';
import {KpiPackage, VisibleAttribute} from '../types/Chart';
import {rem} from 'csx';
import {classes, stylesheet} from 'typestyle';
import {mainContentClassName, spinner, mediaQuery} from '../styles/style';
import DashboardChartContainer from './DashboardChartContainer';
import {BarChartData} from './charts/BarChart';
import {KPISpecialCases, SearchtermKPI, SearchtermKPI as SKPI} from '../kpi';
import {inject, Stores} from '../inject';
import LongtailLimitSelect from './LongtailLimitSelect';
import {I18n, MessageProvider, TProvider} from '../i18n/i18n';
import {DashboardLayoutSlotStyle, makeNextDashboardStyle, NextDashboardStyle} from './DashboardLayout';
import {ChannelStatus} from '../stores/ChannelStore';
import DashboardTableFoldout from './DashboardTableFoldout';
import {
    LightGreyColor,
    LightBlueBackgroundColor,
    BlueBackgroundColor,
    LightBlueColor,
    DarkGreyColor,
    GreenColor,
    RedColor,
    BlackColor,
    CiBlueColor,
    DashboardKpiHoverColor,
    WhiteColor,
    BlackColorFaded10,
    GreyColor,
} from '../styles/colors';
import {ExplorerTitle} from './ExplorerParamsBar/ExplorerParamsBar';

interface ChartConfig {
    key: string;
    isLoading: boolean;
    data: BarChartData[];
    title: string | React.ReactNode;
    vertical: boolean;
    visibleAttributes: VisibleAttribute[];
    chartType?: 'barChart' | 'areaChart';
    kpiPackageToString?: (kpiRequestInfo: KpiPackage, payload: BarChartData) => string | React.ReactNode;
    onTitleClick?: (diagram: DiagramType) => void;
    onBarClick?: (payload: BarChartData) => void;
    titleIsClickable?: boolean;
    style: DashboardLayoutSlotStyle;
}

@observer
class Dashboard extends React.Component<Stores<'store' | 'dashboardStore' | 'timeIntervalStore'>, {}> {
    private getTimeRangeInDays(): number {
        const {startDate, endDate} = this.props.timeIntervalStore.mainDateRange;
        return DateUtils.getDateRangeInDays(startDate, endDate);
    }

    private renameMainCompareAttribute = (kpiPackage: KpiPackage): string | React.ReactNode => {
        const {isCompareDiagram} = kpiPackage;
        if (isCompareDiagram) {
            return (
                <div style={{textDecorationStyle: 'dashed', textDecorationLine: 'underline', display: 'inline'}}>
                    <I18n i18nKey="date.compareRange" />
                </div>
            );
        }
        return <I18n i18nKey="date.mainRange" />;
    };

    private kpiPackageToStringAxisDescription(kpiPackage: KpiPackage, payload: BarChartData, additionalIdentifier: string): string {
        const additionalInformation = payload.data[0].additionalInformation;
        if (additionalInformation) {
            return `${additionalIdentifier} ${additionalInformation.name}`;
        }
        return additionalIdentifier;
    }

    private kpiPackageToStringLongtail = (kpiPackage: KpiPackage, payload: BarChartData): string | React.ReactNode => {
        const additionalInformation = payload.data[0].additionalInformation;
        const {rank = '', percentage = '', searchterm = ''} = additionalInformation || {};
        if (additionalInformation) {
            return (
                <div style={{display: 'inline-block', verticalAlign: 'top'}}>
                    {searchterm}
                    <div className={classes(dashboardCss.bottomTooltipInfo, 'left')}>
                        <I18n i18nKey="dashboard:rank">Rang {{rank}}</I18n>
                    </div>
                    <div className={classes(dashboardCss.bottomTooltipInfo, 'right')}>
                        <div className={classes(dashboardCss.bottomLeftTooltipItem, 'sumSymbol')}>&sum;</div>
                        <div className={dashboardCss.bottomLeftTooltipItem}>{formatValue(percentage, percentFormat())}</div>
                    </div>
                </div>
            );
        }
        return '';
    };

    private getDashboardChartConfigs(t: MessageProvider, width: number): ChartConfig[] {
        const nextDashboardStyle = makeNextDashboardStyle(width);
        return [
            ...this.getTopTenSearchtermDiagrams(nextDashboardStyle),
            ...this.getDailySearchDiagrams(nextDashboardStyle),
            ...this.getHotFlopSearchtermDiagrams(nextDashboardStyle),
            ...this.getMultiWordSearchDiagrams(t, nextDashboardStyle),
            ...this.getLongtailDiagrams(nextDashboardStyle),
        ];
    }

    private getMultiWordSearchDiagrams(t: MessageProvider, nextDashboardStyle: NextDashboardStyle): ChartConfig[] {
        const dashboardStore = this.props.dashboardStore;
        const dashboardData = this.props.dashboardStore.getDashboardDataForCurrentChannel();
        return [
            {
                key: 'searchCountPerWordCount',
                isLoading: dashboardData.multiWordSearchCountAndAvgLoadingTimes.isLoading,
                data: dashboardData.multiWordSearchCountAndAvgLoadingTimes.data,
                title: <I18n i18nKey="dashboard:searchCountPerSearchWordCountDiagramTitle" />,
                vertical: true,
                kpiPackageToString: (kpiPackage: KpiPackage, payload: BarChartData) =>
                    this.kpiPackageToStringAxisDescription(kpiPackage, payload, t('dashboard:searchesDiagramTitle')),
                titleIsClickable: false,
                visibleAttributes: [
                    {
                        kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SearchCount),
                    },
                ],
                style: nextDashboardStyle(false),
            },
            {
                key: 'loadingTimesPerWordCount',
                isLoading: dashboardData.multiWordSearchCountAndAvgLoadingTimes.isLoading,
                data: dashboardData.multiWordSearchCountAndAvgLoadingTimes.data,
                title: <I18n i18nKey="dashboard:avgLoadingTimePerSearchWordCountDiagramTitle" />,
                vertical: true,
                kpiPackageToString: (kpiPackage: KpiPackage, payload: BarChartData) =>
                    this.kpiPackageToStringAxisDescription(kpiPackage, payload, t('dashboard:msLoadingTime')),
                titleIsClickable: false,
                visibleAttributes: [
                    {
                        kpiRequestInfo: dashboardStore.generateKpiPackageFiller(KPISpecialCases.AvgSearchTime),
                    },
                ],
                style: nextDashboardStyle(false),
            },
        ];
    }

    private getHotFlopSearchtermDiagrams(nextDashboardStyle: NextDashboardStyle): ChartConfig[] {
        if (this.props.store.skipSlopes) {
            return [];
        }
        const dashboardStore = this.props.dashboardStore;
        const dayCount = this.getTimeRangeInDays();
        const dashboardData = this.props.dashboardStore.getDashboardDataForCurrentChannel();
        return [
            {
                key: 'flopping10Searchterms',
                isLoading: dashboardData.flopTenSearchterms.isLoading,
                data: dashboardData.flopTenSearchterms.data,
                onBarClick: dashboardStore.flopTenBarClick,
                title: (
                    <I18n i18nKey="dashboard:floppingTenSearchtermsDiagramTitle">
                        Bottom-10 abnehmende Suchbegriffe der ausgewählten {{dayCount}} Tage
                    </I18n>
                ),
                vertical: false,
                kpiPackageToString: this.renameMainCompareAttribute,
                onTitleClick: () => dashboardStore.diagramTitleClicked(DiagramType.FLOP_TEN_SEARCHTERMS),
                visibleAttributes: [
                    {kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SearchCount)},
                    {kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SearchCount, true)},
                ],
                style: nextDashboardStyle(false),
            },
            {
                key: 'trending10Searchterms',
                isLoading: dashboardData.hotTenSearchterms.isLoading,
                data: dashboardData.hotTenSearchterms.data,
                onBarClick: dashboardStore.hotTenBarClick,
                title: (
                    <I18n i18nKey="dashboard:trendingTenSearchtermsDiagramTitle">
                        Top-10 trending Suchbegriffe der ausgewählten {{dayCount}} Tage
                    </I18n>
                ),
                vertical: false,
                kpiPackageToString: this.renameMainCompareAttribute,
                onTitleClick: () => dashboardStore.diagramTitleClicked(DiagramType.HOT_TEN_SEARCHTERMS),
                visibleAttributes: [
                    {kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SearchCount)},
                    {kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SearchCount, true)},
                ],
                style: nextDashboardStyle(true),
            },
        ];
    }

    private getDailySearchDiagrams(nextDashboardStyle: NextDashboardStyle): ChartConfig[] {
        const dashboardStore = this.props.dashboardStore;
        const dashboardData = this.props.dashboardStore.getDashboardDataForCurrentChannel();
        return [
            {
                key: 'salesFromSearches',
                isLoading: dashboardData.dailySales.isLoading,
                data: dashboardData.dailySales.data,
                onBarClick: dashboardStore.salesLabelClicked,
                title: <I18n i18nKey="dashboard:salesFromSearchesDiagramTitle" />,
                vertical: true,
                kpiPackageToString: this.renameMainCompareAttribute,
                onTitleClick: () => dashboardStore.diagramTitleClicked(DiagramType.SALES),
                visibleAttributes: [
                    {
                        kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SalesFromSearches),
                        moneyFormatting: true,
                    },
                    {
                        kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SalesFromSearches, true),
                        moneyFormatting: true,
                    },
                ],
                style: nextDashboardStyle(false),
            },
            {
                key: 'dailySearches',
                isLoading: dashboardData.dailySearches.isLoading,
                data: dashboardData.dailySearches.data,
                onBarClick: dashboardStore.searchLabelClicked,
                title: <I18n i18nKey="dashboard:searchesDiagramTitle" />,
                vertical: true,
                kpiPackageToString: this.renameMainCompareAttribute,
                onTitleClick: () => dashboardStore.diagramTitleClicked(DiagramType.SEARCHES),
                visibleAttributes: [
                    {kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SearchCount)},
                    {kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SearchCount, true)},
                ],
                style: nextDashboardStyle(false),
            },
        ];
    }

    private getTopTenSearchtermDiagrams(nextDashboardStyle: NextDashboardStyle): ChartConfig[] {
        const dashboardStore = this.props.dashboardStore;
        const dayCount = this.getTimeRangeInDays();
        const dashboardData = this.props.dashboardStore.getDashboardDataForCurrentChannel();
        return [
            {
                key: 'top10SearchtermsYesterday',
                isLoading: dashboardData.topTenSearchtermsYesterday.isLoading,
                data: dashboardData.topTenSearchtermsYesterday.data,
                onBarClick: dashboardStore.topTenSearchtermsYesterdayLabelClicked,
                title: <I18n i18nKey="dashboard:top10SearchtermsYesterdayDiagramTitle" />,
                vertical: false,
                kpiPackageToString: this.renameMainCompareAttribute,
                onTitleClick: () => dashboardStore.diagramTitleClicked(DiagramType.TOP_TEN_SEARCHTERMS_YESTERDAY),
                visibleAttributes: [
                    {
                        kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SearchCount),
                    },
                ],
                style: nextDashboardStyle(false),
            },
            {
                key: 'top10SearchtermsMainDateRange',
                isLoading: dashboardData.topTenSearchterms.isLoading,
                data: dashboardData.topTenSearchterms.data,
                onBarClick: dashboardStore.topTenSearchtermsLastXDaysLabelClicked,
                title: <I18n i18nKey="dashboard:top10SearchtermsDiagramTitle">Top-10 Suchbegriffe der ausgewählten {{dayCount}} Tage</I18n>,
                vertical: false,
                kpiPackageToString: this.renameMainCompareAttribute,
                onTitleClick: () => dashboardStore.diagramTitleClicked(DiagramType.TOP_TEN_SEARCHTERMS),
                visibleAttributes: [
                    {kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SearchCount)},
                    {kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SearchCount, true)},
                ],
                style: nextDashboardStyle(true),
            },
        ];
    }

    private getLongtailDiagrams(nextDashboardStyle: NextDashboardStyle): ChartConfig[] {
        const dashboardStore = this.props.dashboardStore;
        const dashboardData = this.props.dashboardStore.getDashboardDataForCurrentChannel();
        return [
            {
                key: 'searchCountLongtail',
                isLoading: dashboardData.longtailStatus[SearchtermKPI.SearchCount].isLoading,
                data: dashboardData.longtailStatus[SearchtermKPI.SearchCount].data,
                chartType: 'areaChart',
                title: (
                    <I18n i18nKey="dashboard:searchCountLongtailDiagramTitle">
                        Top{' '}
                        <LongtailLimitSelect
                            value={dashboardData.longtailSelectionRanges[SKPI.SearchCount]}
                            dashboardStore={dashboardStore}
                            kpi={SKPI.SearchCount}
                        />{' '}
                        Suchanzahl Longtail
                    </I18n>
                ),
                vertical: true,
                kpiPackageToString: this.kpiPackageToStringLongtail,
                onTitleClick: () => dashboardStore.titleClickedLongtailGraphes(SKPI.SearchCount),
                onBarClick: dashboardStore.graphClickedLongtailGraphes,
                visibleAttributes: [
                    {
                        kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.SearchCount),
                    },
                ],
                style: nextDashboardStyle(true),
            },
            {
                key: 'clickCountLongtail',
                isLoading: dashboardData.longtailStatus[SearchtermKPI.ClickCount].isLoading,
                data: dashboardData.longtailStatus[SearchtermKPI.ClickCount].data,
                chartType: 'areaChart',
                title: (
                    <I18n i18nKey="dashboard:clickCountLongtailDiagramTitle">
                        Top{' '}
                        <LongtailLimitSelect
                            value={dashboardData.longtailSelectionRanges[SKPI.ClickCount]}
                            dashboardStore={dashboardStore}
                            kpi={SKPI.ClickCount}
                        />{' '}
                        Klick Longtail
                    </I18n>
                ),
                vertical: true,
                kpiPackageToString: this.kpiPackageToStringLongtail,
                onTitleClick: () => dashboardStore.titleClickedLongtailGraphes(SKPI.ClickCount),
                onBarClick: dashboardStore.graphClickedLongtailGraphes,
                visibleAttributes: [
                    {
                        kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.ClickCount),
                    },
                ],
                style: nextDashboardStyle(false),
            },
            {
                key: 'cartCountLongtail',
                isLoading: dashboardData.longtailStatus[SearchtermKPI.CartCount].isLoading,
                data: dashboardData.longtailStatus[SearchtermKPI.CartCount].data,
                chartType: 'areaChart',
                title: (
                    <I18n i18nKey="dashboard:cartCountLongtailDiagramTitle">
                        Top{' '}
                        <LongtailLimitSelect
                            value={dashboardData.longtailSelectionRanges[SKPI.CartCount]}
                            dashboardStore={dashboardStore}
                            kpi={SKPI.CartCount}
                        />{' '}
                        Warenkorb Longtail
                    </I18n>
                ),
                vertical: true,
                kpiPackageToString: this.kpiPackageToStringLongtail,
                onTitleClick: () => dashboardStore.titleClickedLongtailGraphes(SKPI.CartCount),
                onBarClick: dashboardStore.graphClickedLongtailGraphes,
                visibleAttributes: [
                    {
                        kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.CartCount),
                    },
                ],
                style: nextDashboardStyle(false),
            },
            {
                key: 'checkoutCountLongtail',
                isLoading: dashboardData.longtailStatus[SearchtermKPI.CheckoutCount].isLoading,
                data: dashboardData.longtailStatus[SearchtermKPI.CheckoutCount].data,
                chartType: 'areaChart',
                title: (
                    <I18n i18nKey="dashboard:checkoutCountLongtailDiagramTitle">
                        Top{' '}
                        <LongtailLimitSelect
                            value={dashboardData.longtailSelectionRanges[SKPI.CheckoutCount]}
                            dashboardStore={dashboardStore}
                            kpi={SKPI.CheckoutCount}
                        />{' '}
                        Käufe Longtail
                    </I18n>
                ),
                vertical: true,
                kpiPackageToString: this.kpiPackageToStringLongtail,
                onTitleClick: () => dashboardStore.titleClickedLongtailGraphes(SKPI.CheckoutCount),
                onBarClick: dashboardStore.graphClickedLongtailGraphes,
                visibleAttributes: [
                    {
                        kpiRequestInfo: dashboardStore.generateKpiPackageFiller(SKPI.CheckoutCount),
                    },
                ],
                style: nextDashboardStyle(true),
            },
        ];
    }

    private renderDashboardBody(width: number) {
        const dashboardStore = this.props.dashboardStore;
        if (!dashboardStore.channelState.selectedChannel) {
            return (
                <div className={dashboardCss.container}>
                    <div className={dashboardCss.noChannel}>
                        <I18n i18nKey="dashboard:pleaseSelectAChannel" />
                    </div>
                </div>
            );
        } else {
            return (
                <div className={dashboardCss.container}>
                    <Overview dashboardStore={dashboardStore} store={this.props.store} />
                    <div className={dashboardCss.diagrams}>
                        <TProvider>
                            {(t) =>
                                this.getDashboardChartConfigs(t, width).map((chartConfig) => (
                                    <DashboardChartContainer
                                        key={chartConfig.key}
                                        isLoading={chartConfig.isLoading}
                                        data={chartConfig.data}
                                        chartType={chartConfig.chartType}
                                        onBarClick={chartConfig.onBarClick}
                                        title={chartConfig.title}
                                        vertical={chartConfig.vertical}
                                        kpiPackageToString={chartConfig.kpiPackageToString}
                                        onTitleClick={chartConfig.onTitleClick}
                                        visibleAttributes={chartConfig.visibleAttributes}
                                        style={chartConfig.style}
                                    />
                                ))
                            }
                        </TProvider>
                    </div>
                </div>
            );
        }
    }

    public render() {
        const dashboardStore = this.props.dashboardStore;
        const width = this.props.store.windowWidth - 40 - scrollbarSize();

        return (
            <div className={mainContentClassName}>
                <ChannelDatepickerBar dashboardStore={dashboardStore} />
                {this.renderDashboardBody(width)}
            </div>
        );
    }
}

export default inject('store', 'dashboardStore', 'timeIntervalStore')(Dashboard);

@observer
class Overview extends React.Component<Stores<'dashboardStore' | 'store'>, any> {
    private getValueOrDefault(value: string | number, valueFormat: ValueFormat): string | number {
        return value ? formatValue(value, valueFormat) : 0;
    }

    // tslint:disable-next-line:parameters-max-number
    private renderOverviewItem(
        name: string,
        title: string,
        onClicked: (type: string) => void,
        value: number,
        prevValue: number,
        valueType: ValueFormat,
        unit: string,
        smallerIsBetter: boolean
    ): React.ReactNode {
        let valueStr: React.ReactText;
        let percent = '';
        let trend: 'trend_na' | 'trend_up' | 'trend_down';

        valueStr = this.getValueOrDefault(value, valueType);
        if (prevValue === 0 || isNaN(prevValue)) {
            percent = 'n/v';
            trend = 'trend_na';
        } else if (value < prevValue) {
            percent = '-' + formatValue((prevValue - value) / prevValue, percentFormat());
            trend = smallerIsBetter ? 'trend_up' : 'trend_down';
        } else {
            percent = '+' + formatValue((value - prevValue) / prevValue, percentFormat());
            trend = smallerIsBetter ? 'trend_down' : 'trend_up';
        }

        return (
            <div className={overviewCss.kpi + ' ' + name} key={title}>
                <div className={overviewCss.title}>{title}</div>
                <div className={overviewCss.clickable} onClick={() => onClicked('main')}>
                    <span>
                        <span className={overviewCss.bigNum}>{valueStr}</span>
                        {unit ? <React.Fragment>&nbsp;{unit}</React.Fragment> : ''}
                    </span>
                </div>
                <div className={classes(overviewCss.clickable, overviewCss[trend])} onClick={() => onClicked('compare')}>
                    <span>{percent}</span>
                </div>
            </div>
        );
    }

    private renderOverview(overviewStatus: OverviewStatus): React.ReactNode {
        const {
            channelState: {selectedChannel},
            timeState: {compareDateRange, mainDateRange},
        } = this.props.dashboardStore;

        if (overviewStatus.loading) {
            return (
                <div className={overviewCss.container}>
                    <I18n i18nKey="data.loading" />
                    <div className={spinner(50)} />
                </div>
            );
        }

        if (!selectedChannel) {
            return null;
        }

        const items: React.ReactNode[] = [
            <TProvider key={0}>
                {(t) =>
                    this.renderOverviewItem(
                        'searchCount',
                        t('dashboard:overviewSearchTitle'),
                        this.props.dashboardStore.searchCellClicked,
                        overviewStatus.data.searchCount,
                        overviewStatus.data.searchCountCompare,
                        numberFormat(),
                        '',
                        false
                    )
                }
            </TProvider>,
            <TProvider key={1}>
                {(t) =>
                    this.renderOverviewItem(
                        'searchTime',
                        t('dashboard:overviewSearchTimeTitle') + ' ∅',
                        () => {},
                        overviewStatus.data.searchTime,
                        overviewStatus.data.searchTimeCompare,
                        numberFormat(),
                        t('dashboard:milliSecondsShort'),
                        true
                    )
                }
            </TProvider>,
            <TProvider key={2}>
                {(t) =>
                    this.renderOverviewItem(
                        'totalSales',
                        t('dashboard:overviewSalesTitle'),
                        this.props.dashboardStore.salesClicked,
                        overviewStatus.data.totalSales,
                        overviewStatus.data.totalSalesCompare,
                        numberFormat(2),
                        currencySymbol(selectedChannel),
                        false
                    )
                }
            </TProvider>,
            <TProvider key={3}>
                {(t) =>
                    this.renderOverviewItem(
                        'salesFromSearches',
                        t('dashboard:overviewSalesFromSearchesTitle'),
                        this.props.dashboardStore.salesFromSearchesClicked,
                        overviewStatus.data.salesFromSearches,
                        overviewStatus.data.salesFromSearchesCompare,
                        numberFormat(2),
                        currencySymbol(selectedChannel),
                        false
                    )
                }
            </TProvider>,
            <TProvider key={3}>
                {(t) =>
                    this.renderOverviewItem(
                        'salesFromReco',
                        t('dashboard:overviewSalesFromRecoTitle'),
                        (_) => {},
                        overviewStatus.data.salesFromReco,
                        overviewStatus.data.salesFromRecoCompare,
                        numberFormat(2),
                        currencySymbol(selectedChannel),
                        false
                    )
                }
            </TProvider>,
            <TProvider key={4}>
                {(t) =>
                    this.renderOverviewItem(
                        'salesPerSearch',
                        t('dashboard:overviewSalesSearches') + ' ∅',
                        this.props.dashboardStore.searchCellClicked,
                        overviewStatus.data.salesPerSearch,
                        overviewStatus.data.salesPerSearchCompare,
                        numberFormat(2),
                        currencySymbol(selectedChannel),
                        false
                    )
                }
            </TProvider>,
        ];

        let timeRange = '';
        let duration: React.ReactNode = null;
        let timeRangeCompare = '';

        const {startDate, endDate} = mainDateRange;
        const diff = DateUtils.getDateRangeInDays(startDate, endDate);

        const overviewFoldoutIsVisible = this.props.dashboardStore.overviewFoldoutIsVisible;

        if (diff) {
            timeRange = DateUtils.formatDateRange(startDate, endDate);
            duration = (
                <I18n i18nKey="dashboard:overviewDayCountText" count={diff}>
                    {{dayCount: diff.toString()}} Tage
                </I18n>
            );
            const {startDate: startDateCompare, endDate: endDateCompare} = compareDateRange;
            timeRangeCompare = DateUtils.formatDateRange(startDateCompare, endDateCompare);
        }
        return (
            <div className={overviewCss.container}>
                <div className={overviewCss.centeredContainer}>
                    <div className={overviewCss.containerInner}>
                        <div className={overviewCss.containerInnerRow}>
                            <div className={overviewCss.separator}>{'\xA0'}</div>
                            <div className={overviewCss.dates}>
                                <div>{timeRange}</div>
                                <div>{duration}</div>
                                <div className={overviewCss.prevDates}>{timeRangeCompare}</div>
                            </div>
                            {items}
                        </div>
                        <div className={overviewCss.containerInner}>
                            {overviewFoldoutIsVisible ? (
                                <DashboardTableFoldout
                                    filterTableData={this.props.dashboardStore.getDashboardDataForCurrentChannel().filterTableData}
                                    loadTableFoldoutData={this.props.dashboardStore.loadFoldoutTableData}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
                <div className={overviewCss.moreDataFoldoutMessage}>
                    <a href="#" onClick={() => (this.props.dashboardStore.overviewFoldoutIsVisible = !overviewFoldoutIsVisible)}>
                        {overviewFoldoutIsVisible ? <I18n i18nKey="dashboard:showLessData" /> : <I18n i18nKey="dashboard:showMoreData" />}
                    </a>
                </div>
            </div>
        );
    }

    public render() {
        const dashboardData = this.props.dashboardStore.getDashboardDataForCurrentChannel();
        return <div>{this.renderOverview(dashboardData.overview)}</div>;
    }
}

@observer
class ChannelDatepickerBar extends React.Component<Stores<'dashboardStore'>, {}> {
    private selectedChannelChanged = (selectedChannel: string): void => {
        if (selectedChannel !== null) {
            this.props.dashboardStore.setSelectedChannel(selectedChannel);
        }
    };

    private getSelectedChannel(): string {
        const {selectedChannel} = this.props.dashboardStore.channelState;
        return selectedChannel ? selectedChannel.name : '';
    }

    public render(): React.ReactNode {
        const {
            timeState: {
                mainDateRange: {startDate, endDate},
            },
            channelState: {channelLoadingStatus},
        } = this.props.dashboardStore;
        return (
            <div className={channelDatePickerCss.root}>
                <div style={{paddingRight: scrollbarSize()}}>
                    <div className={channelDatePickerCss.dateRangeContainer}>
                        <div>
                            <ExplorerTitle>
                                <I18n i18nKey="date.mainRange" />
                            </ExplorerTitle>
                            <DateRangeSelector
                                namePrefix="dashboard"
                                startDate={startDate}
                                endDate={endDate}
                                onDatesChange={this.props.dashboardStore.changeDate}
                            />
                        </div>
                    </div>
                    <div className={channelDatePickerCss.channelDropdownContainer}>
                        <div>
                            <ExplorerTitle>
                                <I18n i18nKey="channelSwitcher" />
                            </ExplorerTitle>
                            <TProvider>
                                {(t) => (
                                    <FFDropdownList
                                        isLoading={channelLoadingStatus === ChannelStatus.Loading}
                                        listItems={this.props.dashboardStore.getActiveChannelsDropdownFormat(
                                            t('dashboard:noActiveChannelsAvailableText')
                                        )}
                                        selectedValue={this.getSelectedChannel()}
                                        width={286}
                                        selectedValueChanged={this.selectedChannelChanged}
                                    />
                                )}
                            </TProvider>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const channelDatePickerCss = stylesheet({
    root: {
        padding: '.5rem 20px 1rem 20px',
        borderBottom: '1px solid ' + LightGreyColor,
        backgroundColor: WhiteColor,
    },
    dateRangeContainer: {
        display: 'inline-block',
        verticalAlign: 'top',
    },
    channelDropdownContainer: {
        display: 'inline-block',
        verticalAlign: 'top',
        marginLeft: 10,
        background: WhiteColor,
    },
});

const dashboardCss = stylesheet({
    container: {
        position: 'relative',
        overflowY: 'scroll',
        '-webkit-overflow-scrolling': 'touch',
        backgroundColor: LightBlueBackgroundColor,
        flex: '2',
        textAlign: 'center',
    },
    noChannel: {
        textAlign: 'center',
        paddingTop: rem(3),
    },
    diagrams: {
        padding: '1rem 0',
        marginLeft: 20,
        marginRight: 20,
        position: 'relative',
    },
    bottomTooltipInfo: {
        position: 'absolute',
        fontSize: 12,
        color: BlackColor,
        fontWeight: 'bold',
        bottom: 0,
        $nest: {
            '&.left': {
                left: 5,
            },
            '&.right': {
                right: 5,
            },
        },
    },
    bottomLeftTooltipItem: {
        display: 'inline-block',
        verticalAlign: 'top',
        $nest: {
            '&.sumSymbol': {
                paddingRight: 5,
                fontSize: 10,
                paddingTop: 1,
            },
        },
    },
});

const overviewCss = stylesheet({
    moreDataFoldoutMessage: {
        textAlign: 'right',
        color: CiBlueColor,
        paddingRight: 20,
        paddingTop: rem(0.5),
    },
    centeredContainer: {
        display: 'flex',
        position: 'relative',
        justifyContent: 'center',
    },
    container: {
        borderTop: '1px solid ' + LightBlueColor,
        borderBottom: '1px solid ' + LightBlueColor,
        padding: '1rem 10px',
        display: 'flex',
        position: 'relative',
        margin: '1rem 0',
        backgroundColor: BlueBackgroundColor,
        flexDirection: 'column',
        boxShadow: '0 0 10px ' + BlackColorFaded10,
    },
    containerInnerRow: {
        position: 'relative',
        display: 'flex',
        $nest: {
            '& div': {
                alignSelf: 'flex-end',
                whiteSpace: 'nowrap',
            },
        },
    },
    containerInner: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
    },
    separator: {
        flex: 'none',
        position: 'absolute',
        bottom: '0',
        left: '0',
        right: '0',
        paddingTop: '.1rem',
        borderTop: '1px dotted ' + LightBlueColor,
    },
    dates: {
        whiteSpace: 'nowrap',
        textAlign: 'right',
        paddingRight: 20,
        color: DarkGreyColor,
        maxWidth: 200,
    },
    prevDates: {
        paddingTop: '.6rem',
    },
    bigNum: {
        $nest: {
            '&': mediaQuery('md', '_', {
                fontSize: '150%',
            }),
            // The space is a hack, there is no other way to add multiple mediaQueries to NestedCSSProperties
            '& ': mediaQuery('lg', '_', {
                fontSize: '250%',
            }),
        },
    },
    kpi: {
        paddingLeft: 50,
        $nest: {
            '&.searchTime': mediaQuery('_', 'md', {
                display: 'none',
            }),
        },
    },
    clickable: {
        $nest: {
            '& span': {
                display: 'inline-block',
                borderBottom: '1px solid transparent',
                cursor: 'pointer',
            },
            ':hover': {
                color: DashboardKpiHoverColor,
            },
            ':hover span': {
                borderBottomColor: DashboardKpiHoverColor,
            },
        },
    },
    title: {
        color: DarkGreyColor,
    },
    trend_up: {
        color: GreenColor,
    },
    trend_down: {
        color: RedColor,
    },
    trend_na: {
        color: GreyColor,
    },
});



// WEBPACK FOOTER //
// ./src/views/Dashboard.tsx